import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconClipboardHeart,
  IconPencil,
  IconPlus,
  IconTrash,
  IconWheelchair,
  IconX,
} from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';

import { Patient } from '@interfaces/patients.interface';

import { isApiError } from '@api/index';
import {
  useDeletePatientMutation,
  useGetPatientByIdQuery,
} from '@api/patients.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UserPermissionGuard from '@components/guards/UserPermissionGuard';
import UpsertPatientForm, {
  UPSERT_PATIENT_FORM_MODAL_ID,
} from '@components/patients/UpsertPatientForm';
import TherapiesTable from '@components/therapies/TherapiesTable';
import UpsertTherapyForm, {
  UPSERT_THERAPY_FORM_MODAL_ID,
} from '@components/therapies/UpsertTherapyForm';
import PatientGraphs from '@components/patients/PatientGraphs';

export default function PatientDetails() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { patientId } = useParams();
  const navigate = useNavigate();

  const canUpdatePatient = useUserPermissionGuard('PATIENTS.UPDATE');
  const canDeletePatient = useUserPermissionGuard('PATIENTS.DELETE');

  const canCreateTherapies = useUserPermissionGuard('THERAPIES.CREATE');

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: patient, isLoading: isLoadingPatient } = useGetPatientByIdQuery(
    patientId!,
  );

  const [deletePatient, { isLoading: isLoadingDeletePatient }] =
    useDeletePatientMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleEditPatientClick = useCallback((patient: Patient) => {
    openModal({
      modalId: UPSERT_PATIENT_FORM_MODAL_ID,
      title: 'Modifica paziente',
      children: <UpsertPatientForm patient={patient} />,
      size: '60rem',
    });
  }, []);

  const handleDeletePatientClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina paziente',
      children:
        "Stai per eliminare il paziente. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deletePatient(id).unwrap();

          showNotification({
            title: 'Paziente eliminato',
            message: 'Paziente eliminato con successo',
          });

          navigate('/pazienti');
        } catch (e) {
          if (isApiError(e)) {
            showNotification({
              title: 'Errore',
              message: e.data.message,
              color: 'red',
              icon: <IconX />,
            });
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  const handleAddTherapyClick = useCallback(() => {
    if (!patientId) return;

    openModal({
      modalId: UPSERT_THERAPY_FORM_MODAL_ID,
      title: 'Aggiungi terapia',
      children: (
        <UpsertTherapyForm
          patientId={patientId}
          afterSubmit={({ id }) => navigate(`/terapie/${id}`)}
        />
      ),
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconWheelchair />
        {patient ? `${patient.firstName} ${patient.lastName}` : ''}
      </Group>
    ),
    [patient],
  );

  const backLink = useMemo(
    () => ({
      title: 'Pazienti',
      to: '/pazienti',
    }),
    [],
  );

  useLayoutProps({
    title,
    backLink,
  });

  return isLoadingPatient ? (
    <Loader />
  ) : !patient ? (
    <Text fs="italic">Paziente non trovato o non accessibile</Text>
  ) : (
    <>
      <Paper withBorder p="md" mb="xl">
        <Group justify="space-between" align="start">
          <Group gap="xl" align="start">
            <div>
              <Title order={4} mb="md">
                Informazioni paziente
              </Title>
              <Text>
                <Text fw={700} span>
                  Nome:
                </Text>{' '}
                {patient.firstName}
              </Text>
              <Text>
                <Text fw={700} span>
                  Cognome:
                </Text>{' '}
                {patient.lastName}
              </Text>
              <Text>
                <Text fw={700} span>
                  Titolo:
                </Text>{' '}
                {patient.title}
              </Text>
              {patient.socialSecurityNumber && (
                <Text>
                  <Text fw={700} span>
                    Codice fiscale:
                  </Text>{' '}
                  {patient.socialSecurityNumber}
                </Text>
              )}
              <Text>
                <Text fw={700} span>
                  Data di nascita:
                </Text>{' '}
                {dateToDateString(new Date(patient.birthDate))}
              </Text>
              <Text>
                <Text fw={700} span>
                  Genere:
                </Text>{' '}
                {patient.gender.name}
              </Text>
              <Text>
                <Text fw={700} span>
                  Etnia:
                </Text>{' '}
                {patient.ethnicity.name}
              </Text>
              <Text>
                <Text fw={700} span>
                  Patologie:
                </Text>{' '}
                {patient.pathologies.map((p) => p.name).join(', ')}
              </Text>
              {patient.allergies && (
                <Text>
                  <Text fw={700} span>
                    Allergie:
                  </Text>{' '}
                  {patient.allergies}
                </Text>
              )}
              {patient.mentalState && (
                <Text>
                  <Text fw={700} span>
                    Mental state:
                  </Text>{' '}
                  {patient.mentalState <= 9 && 'Grave'}
                  {patient.mentalState >= 10 &&
                    patient.mentalState <= 18 &&
                    'Moderato'}
                  {patient.mentalState >= 19 &&
                    patient.mentalState <= 23 &&
                    'Lieve'}
                  {patient.mentalState >= 24 && 'Normale'} (
                  {patient.mentalState})
                </Text>
              )}
            </div>

            <div>
              <Title order={4} mb="md">
                Indirizzo
              </Title>

              <Text>
                <Text fw={700} span>
                  Nazione:
                </Text>{' '}
                {patient.countryCode}
              </Text>

              {patient.stateProvince && (
                <Text>
                  <Text fw={700} span>
                    Provincia:
                  </Text>{' '}
                  {patient.stateProvince}
                </Text>
              )}
              <Text>
                <Text fw={700} span>
                  Città:
                </Text>{' '}
                {patient.locality}
              </Text>

              {patient.postalCode && (
                <Text>
                  <Text fw={700} span>
                    CAP:
                  </Text>{' '}
                  {patient.postalCode}
                </Text>
              )}
              <Text>
                <Text fw={700} span>
                  Indirizzo:
                </Text>{' '}
                {patient.streetAddress1}
              </Text>
              {patient.streetAddress2 && (
                <Text>
                  <Text fw={700} span>
                    Indirizzo (2):
                  </Text>{' '}
                  {patient.streetAddress2}
                </Text>
              )}
            </div>

            <div>
              <Title order={4} mb="md">
                Contatti
              </Title>

              {patient.medicalOrganization && (
                <Text>
                  <Text fw={700} span>
                    Organizzazione medica:
                  </Text>{' '}
                  {patient.medicalOrganization?.user.name}
                </Text>
              )}
              {/* TODO: add medical doctor */}
              {patient.email && (
                <Text>
                  <Text fw={700} span>
                    Email:
                  </Text>{' '}
                  {patient.email}
                </Text>
              )}
              {patient.mobilePhone && (
                <Text>
                  <Text fw={700} span>
                    Telefono cellulare:
                  </Text>{' '}
                  {patient.mobilePhone}
                </Text>
              )}
              {patient.homePhone && (
                <Text>
                  <Text fw={700} span>
                    Telefono fisso:
                  </Text>{' '}
                  {patient.homePhone}
                </Text>
              )}
            </div>
          </Group>

          <Stack>
            {canUpdatePatient && (
              <Button
                leftSection={<IconPencil />}
                variant="outline"
                onClick={() => handleEditPatientClick(patient)}
              >
                Modifica
              </Button>
            )}
            {canDeletePatient && (
              <Button
                leftSection={<IconTrash />}
                color="red"
                onClick={() => handleDeletePatientClick(patient.id)}
                loading={isLoadingDeletePatient}
              >
                Elimina
              </Button>
            )}
          </Stack>
        </Group>
      </Paper>

      <Group justify="space-between" align="center" mb="lg">
        <Group>
          <IconClipboardHeart />
          <Title order={3}>Terapie</Title>
        </Group>

        {canCreateTherapies && (
          <Button leftSection={<IconPlus />} onClick={handleAddTherapyClick}>
            Aggiungi terapia
          </Button>
        )}
      </Group>

      <UserPermissionGuard permission="THERAPIES.READ">
        <TherapiesTable patientId={patientId} />
      </UserPermissionGuard>

      <PatientGraphs patientId={patientId!} />
    </>
  );
}
